import TelephoneIcon from '@mirai-kids-monte/core/src/components/dataDisplay/Icon/src/icon-telephone.svg';
import { Address } from '@mirai-kids-monte/core/src/constants/Address';
import { Company } from '@mirai-kids-monte/core/src/constants/Company';
import clsx from 'clsx';
import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';
import { type CSSProperties } from 'react';
import AngleRightIcon from '../../../../public/img/angle-right-icon.svg';
import daycareWantedBannerImage from '../../../../public/img/daycare-wanted-banner.webp';
import MailIcon from '../../../../public/img/mail-icon.svg';
import monteChatBannerImage from '../../../../public/img/monte-chat-banner.webp';
import { Conditions } from '../../../constants/Conditions';
import { FeatureFlags } from '../../../constants/FeatureFlags';
import { PageUrl } from '../../../constants/PageUrl';
import InstagramButton from '../../class_landing_page/atoms/InstagramButton';
import { LazyGoogleMap } from '../../class_landing_page/atoms/LazyGoogleMap';
import LineButton from '../../class_landing_page/atoms/LineButton';
import styles from './index.module.scss';

type Props = {
  variant?: 'mobile' | 'pc';
  maxWidth?: CSSProperties['maxWidth'];
};

type SideMapItem = {
  children?: SideMapItem[];
} & SiteMapItemProps;

const Footer = ({ variant = 'mobile', maxWidth = '1000px' }: Props) => {
  const isMobileVariant = variant === 'mobile';
  const siteMapItems: SideMapItem[] = [
    {
      title: 'トップページ',
      path: PageUrl.TOP,
    },
    {
      title: '神戸のモンテッソーリ保育園 ',
      path: PageUrl.DAYCARE_TOP,
      children: [
        {
          title: '私たちの想い',
          path: PageUrl.DAYCARE_POLICY,
        },
        {
          title: '多様な学び',
          path: PageUrl.DAYCARE_PROGRAMS,
        },
        {
          title: '保育環境',
          path: PageUrl.DAYCARE_FEATURES,
        },
        {
          title: '園での生活',
          path: PageUrl.DAYCARE_LIVING,
        },
        {
          title: '職員紹介',
          path: PageUrl.DAYCARE_TEACHERS,
        },
        {
          title: 'モンテ園体験クラス',
          path: PageUrl.DAYCARE_MONTE_TRIAL_CLASS,
        },
        {
          title: '土曜の教具ひろば',
          path: PageUrl.DAYCARE_SATURDAY_HIROBA,
        },
        {
          title: '入園案内',
          path: PageUrl.DAYCARE_ADMISSION,
        },
        {
          title: '施設紹介',
          path: PageUrl.DAYCARE_FACILITIES,
        },
        {
          title: 'お問い合わせ・見学予約',
          path: PageUrl.DAYCARE_CONTACT,
        },
      ],
    },
    {
      title: '神戸のモンテッソーリ教室 ',
      path: PageUrl.CLASS_TOP,
    },
    {
      title: 'みらいキッズ教具選び支援',
      path: PageUrl.TOOL_RECOMMENDATION_ROOT,
    },
    {
      title: 'みらいキッズモンテチャット (プロ×AI子育て相談)',
      path: PageUrl.MONTE_CHAT_ROOT,
    },
    {
      title: '無料観察サポート診断',
      path: PageUrl.DIAGNOSIS_ROOT,
    },
    {
      title: 'ブログ モンテッソーリ流・子育てのヒント',
      path: PageUrl.BLOG_ROOT,
    },
    {
      title: 'オリジナル教具販売 at minne',
      path: PageUrl.MINNE_TOP,
      newTab: true,
    },
    {
      title: 'プライバシーポリシー',
      path: PageUrl.PRIVACY,
    },
  ];

  const footerContentClassName = isMobileVariant ? styles['footer-content-mobile'] : styles['footer-content-pc'];
  const columnClassName = isMobileVariant ? styles['column-mobile'] : styles['column-pc'];
  const logoClassName = isMobileVariant ? styles['logo-image-mobile'] : styles['logo-image-pc'];

  return (
    <footer className={styles.base}>
      <div className={styles['footer-inner']} style={{ maxWidth }}>
        <div>
          <img src="/img/logo3.svg" alt="みらいキッズモンテ" className={logoClassName} width={227} height={25} />
          <p className={styles['body-text']}>
            みらいキッズモンテプリスクールは神戸市灘区のモンテッソーリ保育園・教室です。保育園・教室運営の他、おうちモンテ導入の支援や教具販売を行なっています。お子様一人一人の興味関心を刺激し、子どもが自ら成長できるサイクルを作るお手伝いをいたします。
          </p>
        </div>
        <div className={footerContentClassName}>
          <div className={columnClassName}>
            <div>
              <h2 className={styles.title}>お問い合わせ</h2>
              <h3 className={styles['sub-title']}>SNS</h3>
              <div className={styles['sns-wrapper']}>
                <LineButton position="footer">
                  公式LINEへ
                  <br />
                  お問い合わせ
                </LineButton>
                <InstagramButton />
              </div>
              <h3 className={styles['sub-title']}>その他</h3>
              <BasicContactInformation variant={variant} />
            </div>
          </div>
          <div className={columnClassName}>
            <div>
              <h2 className={styles.title}>アクセス</h2>
              <ul className={styles['access-contents-wrapper']}>
                <li className={styles['body-text']}>
                  {Address.MONTE_SPACE}
                  <Link
                    key={PageUrl.GOOGLE_MAP_MONTE_SPACE}
                    href={PageUrl.GOOGLE_MAP_MONTE_SPACE}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles['text-link']}
                  >
                    Google mapでみる
                  </Link>
                </li>
              </ul>
              <LazyGoogleMap
                autoLoad
                width="100%"
                height={300}
                mapCenterCoordinate={{
                  lat: 34.71034709736502,
                  lng: 135.22541341979556,
                }}
                markerCoordinates={[Conditions.monteSpace]}
              />
            </div>
          </div>
          <div className={columnClassName}>
            <div>
              <h2 className={styles.title}>サイトマップ</h2>
              <div className={styles['site-map-items']}>
                {siteMapItems.map((item) => (
                  <div key={item.title}>
                    <SiteMapItem title={item.title} path={item.path} newTab={item.newTab} />
                    <div className={styles['site-map-child-items']}>
                      {item.children?.map((childItem) => (
                        <SiteMapItem
                          key={childItem.title}
                          title={childItem.title}
                          path={childItem.path}
                          newTab={childItem.newTab}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={columnClassName}>
            <div>
              <h2 className={styles.title}>お知らせ</h2>
              <Link href={PageUrl.MONTE_CHAT_ROOT}>
                <ExportedImage
                  className={styles['banner-image']}
                  src={monteChatBannerImage}
                  alt="みらいキッズモンテチャット - プロとAIがあなただけの子育てをサポート"
                  width={600}
                  height={200}
                  loading="lazy"
                />
              </Link>
              {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
              {FeatureFlags.SHOW_DAYCARE_RECRUITMENT_BANNER ? (
                <Link key={PageUrl.DAYCARE_TOP} href={PageUrl.DAYCARE_TOP}>
                  <ExportedImage
                    className={styles['banner-image']}
                    src={daycareWantedBannerImage}
                    alt="モンテッソーリ認可外保育園の園児募集のお知らせ"
                    width={600}
                    height={200}
                    loading="lazy"
                  />
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.copyright}>
        <p>&copy; 2022 みらいキッズモンテプリスクール</p>
      </div>
    </footer>
  );
};

export default Footer;

type SiteMapItemProps = {
  title: string;
  path: string;
  newTab?: boolean;
};

const SiteMapItem = ({ title, path, newTab = false }: SiteMapItemProps) => (
  <Link
    href={path}
    target={newTab ? '_blank' : undefined}
    rel={newTab ? 'noopener noreferrer' : undefined}
    className={styles['site-map-item-base']}
  >
    <AngleRightIcon className={styles['site-map-icon-wrapper']} />
    <span className={styles['site-map-item-title']}>{title}</span>
  </Link>
);

type BasicContactInformationProps = {
  variant: 'mobile' | 'pc';
};

const BasicContactInformation = ({ variant = 'mobile' }: BasicContactInformationProps) => {
  const isPc = variant === 'pc';
  const iconClassName = isPc ? styles['contact-icon-pc'] : styles['contact-icon-mobile'];
  const telephoneIconClassName = isPc ? styles['telephone-icon-pc'] : styles['telephone-icon-mobile'];
  const spacingClassName = isPc ? styles['icon-with-text-pc'] : styles['icon-with-text-mobile'];

  const contactItems = [
    {
      key: 'email',
      href: `mailto:${Company.EMAIL}`,
      icon: <MailIcon className={iconClassName} />,
      text: Company.EMAIL,
    },
    {
      key: 'phone',
      href: `tel:${Company.PHONE.replace(/-/g, '')}`,
      icon: <TelephoneIcon className={telephoneIconClassName} />,
      text: Company.PHONE,
    },
  ];

  return (
    <div className={styles['contact-info-base']}>
      {contactItems.map((item) => {
        const className = clsx(styles['icon-with-text'], spacingClassName);
        const content = (
          <>
            {item.icon}
            <p className={styles['contact-text']}>{item.text}</p>
          </>
        );

        return (
          <a key={item.key} href={item.href} target="_blank" rel="noopener noreferrer" className={className}>
            {content}
          </a>
        );
      })}
    </div>
  );
};
